import { useMemo } from 'react';
import { useReadLocalStorage } from 'usehooks-ts';
import type { ProfileResponse, UploadStorageSettings } from '../types';
import ApiClient from './ApiClient';

export type CopyProgress = {
  pending: number;
  moved: number;
  noped: number;
  errored: number;
  error_details: Record<string, { message: string; backtrace: string }>;
};

class InternalClient extends ApiClient {
  token: string;

  constructor(token: string) {
    super(
      process.env.NODE_ENV === 'development'
        ? 'http://internal.lvh.me:3001'
        : 'https://internal.datocms.com',
      {},
      { Authorization: `Bearer ${token}` },
    );
    this.token = token;
  }

  getProfile(info: string) {
    return this.get<ProfileResponse>('/customers/profile', { info });
  }

  deactivate2Fa(accountId: string) {
    return this.put(`/customers/${accountId}/reset-2fa`, {});
  }

  trashAccount(accountId: string) {
    return this.put(`/customers/${accountId}/trash-account`, {});
  }

  resetAccountInvitationEmailsBudget(accountId: string) {
    return this.put(
      `/customers/${accountId}/reset-invitation-emails-budget`,
      {},
    );
  }

  resetOrganizationInvitationEmailsBudget(organizationId: string) {
    return this.put(
      `/organizations/${organizationId}/reset-invitation-emails-budget`,
      {},
    );
  }

  reactivateProject(siteId: string) {
    return this.put(`/sites/${siteId}/reactivate`, {});
  }

  deactivateProject(siteId: string) {
    return this.put(`/sites/${siteId}/deactivate`, {});
  }

  changeAccountEmail(accountId: string, email: string) {
    return this.put(`/customers/${accountId}/change-email`, { email });
  }

  verifyUploadStorageSettings(
    siteId: string,
    skip_label_detection_test: boolean,
    uploadStorageSettings: UploadStorageSettings,
  ) {
    return this.post(
      `/upload-storage-settings/${siteId}/verify${
        skip_label_detection_test ? '?skip_label_detection_test=true' : ''
      }`,
      uploadStorageSettings,
    );
  }

  copyUploadsUsingUploadStorageSettings(
    siteId: string,
    skip_label_detection_test: boolean,
    uploadStorageSettings: UploadStorageSettings,
  ): Promise<{ uuid: string }> {
    return this.post(
      `/upload-storage-settings/${siteId}/copy${
        skip_label_detection_test ? '?skip_label_detection_test=true' : ''
      }`,
      uploadStorageSettings,
    );
  }

  getCopyUploadsProgress(siteId: string, uuid: string): Promise<CopyProgress> {
    return this.get(`/upload-storage-settings/${siteId}/copy-progress/${uuid}`);
  }

  updateUploadStorageSettings(
    siteId: string,
    skip_label_detection_test: boolean,
    uploadStorageSettings: UploadStorageSettings,
  ): Promise<{ uuid: string }> {
    return this.put(
      `/upload-storage-settings/${siteId}${
        skip_label_detection_test ? '?skip_label_detection_test=true' : ''
      }`,
      uploadStorageSettings,
    );
  }
}

export function useInternalClient() {
  const token = useReadLocalStorage<string>('customerToken')!;
  return useMemo(() => new InternalClient(token), [token]);
}
